import {
    DataSnapshot,
    get,
    onValue,
    ref,
    serverTimestamp,
    set,
    Unsubscribe,
    update,
} from 'firebase/database'
import {
    BookingReplacementState,
    BookingSchoolNotes,
    BookingSupportDetails,
    BookingTerm,
    PlacementDetailsState,
    ReinstateBookingProps,
    ReinstateBookingResponse,
    SchoolBooking,
    SchoolBookingType,
} from './types-bookings'
import { db, functions } from './configs'
import { BookingParams, LocationParams, PLACEMENT_REF } from './school-api'
import {
    BookingStatusCellType,
    BookingStatusCellTypes,
} from '../pages/Dashboard/schools/BookingsPage/tableComponents/BookingStatusCell'
import dayjs from 'dayjs'
import { SchoolFeedbackForm } from '../pages/Dashboard/schools/components/BookingDetail'
import { httpsCallable } from 'firebase/functions'
import { logger } from '../helpers/logger'

export type BookingTimes = {
    start: string | null
    finish: string | null
}

export const BOOKINGS_API = {
    //#region Queries
    exists: async (args: LocationParams & BookingParams): Promise<boolean> =>
        (await get(ref(db, PLACEMENT_REF(args)))).exists(),
    fetchChild: async <T>(
        args: LocationParams & BookingParams,
        key: keyof SchoolBooking
    ) => {
        return (
            await get(ref(db, `${PLACEMENT_REF(args)}/${key}`))
        ).val() as T | null
    },

    fetchBooking: async (
        args: LocationParams & BookingParams
    ): Promise<SchoolBooking | null> => {
        logger.debug('fetching booking...')
        return (
            await get(ref(db, `${PLACEMENT_REF(args)}`))
        ).val() as SchoolBooking | null
    },
    fetchReplacementState: async (
        args: LocationParams & BookingParams
    ): Promise<BookingReplacementState | null> => {
        return (
            await get(
                ref(db, `${PLACEMENT_REF(args)}/replacementDetails/state`)
            )
        ).val()
    },

    fetchTerm: async (
        args: LocationParams & BookingParams
    ): Promise<BookingTerm | null> =>
        (
            await get(ref(db, `${PLACEMENT_REF(args)}/placementDetails/term`))
        ).val(),

    fetchType: async (
        args: LocationParams & BookingParams
    ): Promise<SchoolBookingType | null> =>
        (
            await get(ref(db, `${PLACEMENT_REF(args)}/placementDetails/type`))
        ).val() as SchoolBookingType | null,

    fetchTimes: async (
        args: BookingParams & LocationParams
    ): Promise<BookingTimes> => {
        const res = await Promise.all([
            get(ref(db, `${PLACEMENT_REF(args)}/placementDetails/startTime`)),
            get(ref(db, `${PLACEMENT_REF(args)}/placementDetails/finishTime`)),
        ])
        return {
            start: res[0].val(),
            finish: res[1].val(),
        }
    },

    fetchReplacingSubject: async (
        args: BookingParams & LocationParams
    ): Promise<string | null> =>
        (
            await get(
                ref(db, `${PLACEMENT_REF(args)}/schoolNotes/subjectReplacing`)
            )
        ).val(),
    fetchReplacingName: async (
        args: BookingParams & LocationParams
    ): Promise<string | null> =>
        (
            await get(
                ref(
                    db,
                    `${PLACEMENT_REF(args)}/schoolNotes/replacingTeacherNamed`
                )
            )
        ).val(),

    fetchTeacherFirstName: async (
        args: BookingParams & LocationParams
    ): Promise<string | null> =>
        (
            await get(
                ref(db, `${PLACEMENT_REF(args)}/teacherDetails/firstName`)
            )
        ).val(),

    fetchBookingAutoReplaceSetting: async (
        args: LocationParams & BookingParams
    ): Promise<boolean> =>
        (
            await get(
                ref(db, `${PLACEMENT_REF(args)}/placementDetails/autoReplace`)
            )
        ).val() || false,
    fetchPlacementState: async (
        args: LocationParams & BookingParams
    ): Promise<PlacementDetailsState | null> =>
        (
            await get(ref(db, `${PLACEMENT_REF(args)}/placementDetails/state`))
        ).val(),

    fetchStatus: async (
        args: LocationParams & BookingParams & { cancelled?: boolean }
    ): Promise<BookingStatusCellType | null> => {
        let value: BookingStatusCellType | null = null
        try {
            if (args.cancelled) {
                // cancelled -> need to check if finding replacement (check replacementDetails/state)
                const replacementState = (
                    await get(
                        ref(
                            db,
                            `${PLACEMENT_REF(args)}/replacementDetails/state`
                        )
                    )
                ).val() as BookingReplacementState | null
                switch (replacementState) {
                    case 'inProgress': {
                        value = BookingStatusCellTypes.findingReplacement
                        break
                    }
                    case 'closed':
                    case 'complete': {
                        value = BookingStatusCellTypes.cancelled
                        break
                    }
                    default: {
                        /**
                         * NOTE: This handles case for the slight delay
                         * in replacement state to populate when booking is cancelled by teacher
                         * due to Cloud Functions cold starts
                         */
                        const [autoReplace, placementState] = await Promise.all(
                            [
                                BOOKINGS_API.fetchBookingAutoReplaceSetting(
                                    args
                                ),
                                BOOKINGS_API.fetchPlacementState(args),
                            ]
                        )
                        if (
                            placementState === 'cancelledByTeacher' &&
                            !!autoReplace
                        ) {
                            value = BookingStatusCellTypes.findingReplacement
                        } else {
                            value = BookingStatusCellTypes.cancelled
                        }
                    }
                }
            } else if (args.dateObj.isAfter(dayjs(), 'date')) {
                // not cancelled for future dates -> confirmed
                value = BookingStatusCellTypes.confirmed
            } else {
                // not cancelled for today -> check teacherDetails/state
                value = (
                    await get(
                        ref(db, `${PLACEMENT_REF(args)}/teacherDetails/state`)
                    )
                ).val()
            }

            return value
        } catch (e) {
            return null
        }
    },

    fetchTeacherVIT: async (
        args: BookingParams & LocationParams
    ): Promise<string | null> =>
        (
            await get(
                ref(db, `${PLACEMENT_REF(args)}/teacherDetails/vitReference`)
            )
        ).val(),

    fetchAdditionalDetails: async (
        args: BookingParams & LocationParams
    ): Promise<BookingSchoolNotes | null> =>
        (await get(ref(db, `${PLACEMENT_REF(args)}/schoolNotes`))).val(),

    fetchTeacherCancelReason: async (
        args: BookingParams & LocationParams
    ): Promise<string | null> =>
        (
            await get(
                ref(
                    db,
                    `${PLACEMENT_REF(args)}/teacherDetails/schoolCancelReason`
                )
            )
        ).val(),

    fetchSchoolCancelTimestamp: async (
        args: BookingParams & LocationParams
    ): Promise<string | null> =>
        (
            await get(
                ref(
                    db,
                    `${PLACEMENT_REF(args)}/placementDetails/schoolCancelTimeStamp`
                )
            )
        ).val(),

    //#endregion

    //#region Mutations

    supportDetails: {
        update: (
            args: BookingParams & LocationParams,
            data: Partial<BookingSupportDetails>
        ) => update(ref(db, `${PLACEMENT_REF(args)}/supportDetails`), data),
    },
    schoolNotes: {
        update: (
            args: BookingParams & LocationParams,
            data: Partial<BookingSchoolNotes>
        ) => update(ref(db, `${PLACEMENT_REF(args)}/schoolNotes`), data),
    },
    teacherFeedback: {
        markAsViewed: (args: BookingParams & LocationParams, userUid: string) =>
            set(
                ref(
                    db,
                    `${PLACEMENT_REF(args)}/teacherFeedback/schoolUsersNotified/${userUid}`
                ),
                serverTimestamp()
            ),
    },

    setAutoreplaceSetting: (
        args: BookingParams & LocationParams,
        value: boolean
    ) =>
        set(
            ref(db, `${PLACEMENT_REF(args)}/placementDetails/autoReplace`),
            value
        ),

    reinstate: (args: ReinstateBookingProps) =>
        httpsCallable<ReinstateBookingProps, ReinstateBookingResponse>(
            functions,
            'reinstateBooking'
        )(args),

    setAdditionalDetails: async (
        args: BookingParams & LocationParams,
        data: Required<BookingSchoolNotes>
    ) => {
        return set(ref(db, `${PLACEMENT_REF(args)}/schoolNotes`), data)
    },
    submitSchoolFeedback: (
        args: LocationParams & BookingParams,
        userUid: string,
        data: SchoolFeedbackForm & {
            userName: string
        }
    ) => {
        return set(
            ref(db, `${PLACEMENT_REF(args)}/schoolFeedback/${userUid}`),
            {
                ...Object.entries(data).reduce(
                    (a, c) => ({
                        ...a,
                        [c[0]]: c[1] === undefined ? null : c[1],
                    }),
                    {} as Record<string, any>
                ),
                timeStamp: serverTimestamp(),
            }
        )
    },
    //#endregion

    //#region Live Listeners
    listenBookingTeacherState: (
        args: LocationParams & BookingParams,
        callback: (snap: DataSnapshot) => void
    ): Unsubscribe => {
        return onValue(
            ref(db, `${PLACEMENT_REF(args)}/teacherDetails/state`),
            callback
        )
    },
    listenBookingState: (
        args: LocationParams & BookingParams,
        callback: (snap: DataSnapshot) => void
    ): Unsubscribe => {
        return onValue(
            ref(db, `${PLACEMENT_REF(args)}/placementDetails/state`),
            callback
        )
    },
    listenBookingReplacementState: (
        args: LocationParams & BookingParams,
        callback: (snap: DataSnapshot) => void
    ): Unsubscribe => {
        return onValue(
            ref(db, `${PLACEMENT_REF(args)}/replacementDetails/state`),
            callback
        )
    },
    listenAutoReplaceSetting: (
        args: LocationParams & BookingParams,
        callback: (snap: DataSnapshot) => void
    ): Unsubscribe => {
        return onValue(
            ref(db, `${PLACEMENT_REF(args)}/placementDetails/autoReplace`),
            callback
        )
    },
    //#endregion
} as const
